import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Search, Save, Loader2 } from 'lucide-react';
import { DEFAULT_PAGE_META, SEO_PAGE_LABELS } from '@/components/SeoManager';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { settingsApi } from '@/integrations/mysql/client';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

type PageSeo = { title: string; description: string };
type SeoDraft = Record<string, PageSeo>;

function readSeoSettings(value: string | undefined): SeoDraft {
  let saved: Record<string, Partial<PageSeo>> = {};
  try {
    const parsed = JSON.parse(value || '{}');
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) saved = parsed;
  } catch { /* Use defaults if stored JSON is invalid. */ }

  return Object.fromEntries(Object.entries(SEO_PAGE_LABELS).map(([path]) => [path, {
    title: saved[path]?.title || DEFAULT_PAGE_META[path].title,
    description: saved[path]?.description || DEFAULT_PAGE_META[path].description,
  }]));
}

export function SeoSettingsManager() {
  const { settings, isLoading } = useSiteSettings();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [draft, setDraft] = useState<SeoDraft>(() => readSeoSettings(undefined));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isLoading) setDraft(readSeoSettings(settings.seo_pages_meta));
  }, [isLoading, settings.seo_pages_meta]);

  const original = useMemo(() => readSeoSettings(settings.seo_pages_meta), [settings.seo_pages_meta]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(original);

  const update = (path: string, field: keyof PageSeo, value: string) => {
    setDraft(previous => ({
      ...previous,
      [path]: { ...previous[path], [field]: value },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update('seo_pages_meta', JSON.stringify(draft));
      await queryClient.invalidateQueries({ queryKey: ['site-settings'] });
      toast({ title: 'SEO salvo!', description: 'Títulos e descrições serão usados nas páginas públicas.' });
    } catch (error) {
      toast({ title: 'Erro ao salvar SEO', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
        <p className="font-semibold flex items-center gap-2"><Search className="h-4 w-4" /> Títulos e descrições para buscadores</p>
        <p className="mt-1 text-blue-800">Escreva um título específico e uma descrição clara para cada página. O Google pode adaptar o texto exibido nos resultados.</p>
      </div>

      {Object.entries(SEO_PAGE_LABELS).map(([path, label]) => (
        <section key={path} className="space-y-3 rounded-xl border border-gray-100 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-semibold text-gray-800">{label}</h3>
            <span className="text-xs text-gray-400">{path}</span>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Título</label>
            <Input value={draft[path]?.title ?? ''} onChange={e => update(path, 'title', e.target.value)} maxLength={80} />
            <p className="mt-1 text-right text-xs text-gray-400">{draft[path]?.title.length ?? 0}/80</p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Descrição</label>
            <Textarea value={draft[path]?.description ?? ''} onChange={e => update(path, 'description', e.target.value)} maxLength={300} rows={2} />
            <p className="mt-1 text-right text-xs text-gray-400">{draft[path]?.description.length ?? 0}/300</p>
          </div>
        </section>
      ))}

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={handleSave} disabled={!dirty || saving || isLoading}
          className="flex items-center gap-2 rounded-xl bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-brand-primary/90 disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Salvar SEO
        </button>
      </div>
    </div>
  );
}
